import React, { createContext, useContext, useCallback, ReactNode, useState, useEffect } from 'react';
import { useUserSubscription } from './UserSubscriptionContext';
import { permissionService, PermissionDisplayMetadata } from '../services/permissionService';

// Feature type is now dynamic - accepts any string for future-proof feature additions
export type Feature = string;

interface PermissionContextValue {
  tier: string;
  features: Record<string, boolean>;
  hasFeature: (feature: Feature) => boolean;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  permissionsMetadata: PermissionDisplayMetadata[];
  getPermissionMetadata: (feature: Feature) => PermissionDisplayMetadata | undefined;
  getSubscriptionFeatures: () => PermissionDisplayMetadata[];
}

const PermissionContext = createContext<PermissionContextValue | undefined>(undefined);

interface PermissionProviderProps {
  children: ReactNode;
}

export function PermissionProvider({ children }: PermissionProviderProps) {
  // Use subscription data from UserSubscriptionContext instead of fetching independently
  const { 
    subscriptionStatus, 
    isLoading: subscriptionLoading, 
    error: subscriptionError, 
    refresh: refreshSubscription 
  } = useUserSubscription();

  // State for permission display metadata
  const [permissionsMetadata, setPermissionsMetadata] = useState<PermissionDisplayMetadata[]>([]);
  const [metadataLoading, setMetadataLoading] = useState<boolean>(true);
  const [metadataError, setMetadataError] = useState<string | null>(null);

  // Fetch permission display metadata on mount
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        setMetadataLoading(true);
        setMetadataError(null);
        // Fetch all permissions (not filtered) so we have complete metadata
        const metadata = await permissionService.getDisplayMetadata();
        setPermissionsMetadata(metadata);
      } catch (err: any) {
        setMetadataError(err?.message || 'Failed to load permission metadata');
        // Don't fail completely - continue with empty metadata
        setPermissionsMetadata([]);
      } finally {
        setMetadataLoading(false);
      }
    };

    fetchMetadata();
  }, []);

  const hasFeature = useCallback((feature: Feature): boolean => {
    if (!subscriptionStatus?.features) {
      // No features available - return false for all features
      // Features are now fully dynamic from the database
      return false;
    }
    // Explicitly check for true, defaulting to false for undefined/null/false values
    const featureValue = subscriptionStatus.features[feature];
    return featureValue === true;
  }, [subscriptionStatus]);

  const getPermissionMetadata = useCallback((feature: Feature): PermissionDisplayMetadata | undefined => {
    return permissionsMetadata.find(p => p.name === feature);
  }, [permissionsMetadata]);

  const getSubscriptionFeatures = useCallback((): PermissionDisplayMetadata[] => {
    return permissionsMetadata
      .filter(p => p.show_in_subscription_table)
      .sort((a, b) => a.display_order - b.display_order);
  }, [permissionsMetadata]);

  const tier = subscriptionStatus?.subscription_tier || 'free';

  const value: PermissionContextValue = {
    tier,
    features: subscriptionStatus?.features || {}, // Empty object - features come from API
    hasFeature,
    isLoading: subscriptionLoading || metadataLoading,
    error: subscriptionError || metadataError,
    refresh: refreshSubscription,
    permissionsMetadata,
    getPermissionMetadata,
    getSubscriptionFeatures,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissions(): PermissionContextValue {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
}

/**
 * Standalone PermissionProvider for use on landing pages or other contexts
 * where authentication/subscription context is not available.
 * This provider only fetches permission metadata and defaults to free tier.
 */
export function StandalonePermissionProvider({ children }: PermissionProviderProps) {
  // State for permission display metadata
  const [permissionsMetadata, setPermissionsMetadata] = useState<PermissionDisplayMetadata[]>([]);
  const [metadataLoading, setMetadataLoading] = useState<boolean>(true);
  const [metadataError, setMetadataError] = useState<string | null>(null);

  // Default free tier features - empty object since features are now dynamic from API
  const defaultFeatures: Record<string, boolean> = {};

  // Fetch permission display metadata on mount
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        setMetadataLoading(true);
        setMetadataError(null);
        // Fetch all permissions (not filtered) so we have complete metadata
        const metadata = await permissionService.getDisplayMetadata();
        setPermissionsMetadata(metadata);
      } catch (err: any) {
        setMetadataError(err?.message || 'Failed to load permission metadata');
        // Don't fail completely - continue with empty metadata
        setPermissionsMetadata([]);
      } finally {
        setMetadataLoading(false);
      }
    };

    fetchMetadata();
  }, []);

  const hasFeature = useCallback((feature: Feature): boolean => {
    // Default to free tier features for landing page
    return defaultFeatures[feature] ?? false;
  }, [defaultFeatures]);

  const getPermissionMetadata = useCallback((feature: Feature): PermissionDisplayMetadata | undefined => {
    return permissionsMetadata.find(p => p.name === feature);
  }, [permissionsMetadata]);

  const getSubscriptionFeatures = useCallback((): PermissionDisplayMetadata[] => {
    return permissionsMetadata
      .filter(p => p.show_in_subscription_table)
      .sort((a, b) => a.display_order - b.display_order);
  }, [permissionsMetadata]);

  const refresh = useCallback(async () => {
    // No-op for standalone provider
    return Promise.resolve();
  }, []);

  const value: PermissionContextValue = {
    tier: 'free',
    features: defaultFeatures,
    hasFeature,
    isLoading: metadataLoading,
    error: metadataError,
    refresh,
    permissionsMetadata,
    getPermissionMetadata,
    getSubscriptionFeatures,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

